<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[600px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-lg tw-my-5 tw-rounded-lg">
            <!-- Header -->
            <div class="tw-p-6 tw-border-b tw-border-solid tw-border-gray-200 tw-flex tw-justify-between tw-items-center">
                <h3 class="tw-text-lg tw-font-semibold tw-text-gray-800">{{ $t('forms.certificate.create-title') }}</h3>
                <button @click="onCancel" class="tw-text-gray-400 hover:tw-text-gray-600 tw-duration-200">
                    <icon icon="solar:close-line-linear" class="tw-text-xl" />
                </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="onSubmit" class="tw-p-6">
                <div class="tw-space-y-6">
                    <!-- Name Field -->
                    <FormField
                        v-model="form.name"
                        type="text"
                        :label="$t('forms.certificate.name')"
                        :placeholder="$t('forms.certificate.name-placeholder')"
                        :error="errors.name"
                        required
                    />

                    <!-- Description Field -->
                    <FormField
                        v-model="form.description"
                        type="textarea"
                        :label="$t('forms.certificate.description')"
                        :placeholder="$t('forms.certificate.description-placeholder')"
                        :error="errors.description"
                        rows="3"
                    />

                    <!-- Type Field -->
                    <FormSelect
                        v-model="form.type"
                        :options="typeOptions"
                        :label="$t('forms.certificate.type')"
                        :placeholder="$t('forms.certificate.type-placeholder')"
                        :error="errors.type"
                    />

                    <!-- Status Field -->
                    <FormSelect
                        v-model="form.status"
                        :options="statusOptions"
                        :label="$t('forms.certificate.status')"
                        :placeholder="$t('forms.certificate.status-placeholder')"
                        :error="errors.status"
                        required
                    />
                </div>

                <!-- Form Actions -->
                <div class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-8 tw-pt-6 tw-border-t tw-border-gray-200">
                    <a-button 
                        type="button" 
                        intent="simple" 
                        @click="onCancel"
                        :disabled="isSubmitting"
                    >
                        {{ $t('buttons.cancel') }}
                    </a-button>
                    <a-button 
                        type="submit" 
                        intent="primary" 
                        class="tw-min-w-[160px]"
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                    >
                        {{ $t('buttons.create') }}
                    </a-button>
                </div>
            </form>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, reactive, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import certificatesApi from '@/api/certificates';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close', 'created']);

// Form data
const form = reactive({
    name: '',
    description: '',
    type: '',
    status: 'active'
});

// Form state
const isSubmitting = ref(false);
const errors = reactive({});

// Options
const typeOptions = ref([
    { id: '', name: t('common.select-type', 'Select type') },
    { id: 'product', name: t('common.product', 'Product') },
    { id: 'service', name: t('common.service', 'Service') },
    { id: 'company', name: t('common.company', 'Company') },
    { id: 'general', name: t('common.general', 'General') }
]);

const statusOptions = ref([
    { id: 'active', name: t('common.active') },
    { id: 'inactive', name: t('common.inactive') }
]);

// Form validation
const validateForm = () => {
    errors.value = {};

    if (!form.name.trim()) {
        errors.name = t('errors.required', { field: t('forms.certificate.name') });
        return false;
    }

    if (form.name.trim().length < 2) {
        errors.name = t('errors.min-length', { field: t('forms.certificate.name'), min: 2 });
        return false;
    }

    if (form.description && form.description.trim().length > 500) {
        errors.description = t('errors.max-length', { field: t('forms.certificate.description'), max: 500 });
        return false;
    }

    return true;
};

// Methods
const onSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    isSubmitting.value = true;

    try {
        const response = await certificatesApi.admin.store({
            name: form.name.trim(),
            description: form.description.trim() || null,
            type: form.type || null,
            status: form.status
        });

        if (response.data && response.data.success) {
            emit('created', response.data.certificate || response.data.data);
            resetForm();
        } else {
            useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        }
    } catch (error) {
        console.error('Error creating certificate:', error);
        
        if (error.response?.status === 422) {
            // Handle validation errors from server
            const serverErrors = error.response.data.errors;
            Object.keys(serverErrors).forEach(key => {
                errors[key] = serverErrors[key][0];
            });
        } else {
            useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        }
    } finally {
        isSubmitting.value = false;
    }
};

const onCancel = () => {
    if (!isSubmitting.value) {
        resetForm();
        emit('close');
    }
};

const resetForm = () => {
    form.name = '';
    form.description = '';
    form.type = '';
    form.status = 'active';
    errors.value = {};
};

// Watch for modal visibility changes
watch(() => props.visible, (newValue) => {
    if (newValue) {
        resetForm();
    }
});
</script>

<style scoped>
/* Add any custom styles here */
</style> 